import React, { FC } from 'react'

import { alpha, useTheme, Box } from '@mui/material'
import { isEmpty } from 'lodash'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { chartOptions } from '../chartOptions'
import { EmptyData } from '../EmptyData'
import { Indicators } from '../Indicators'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'
import { WinLossAmountType } from '../types'

export const WinLossMoneyWidget: FC<{
  data: WinLossAmountType[] | undefined
  displayWidth: number
  currency: string | undefined
}> = ({ data, currency, displayWidth }) => {
  const { spacing, palette } = useTheme()
  const { t } = useTranslation()

  const chartData = {
    labels: data?.map((item) => item.period),
    datasets: [
      {
        label: t('wins', 'Wins'),
        data: data?.map((item) => item.win.money),
        backgroundColor: alpha(palette.green[400] as string, 0.3),
        fill: true,
        borderColor: palette.green[400],
        borderWidth: 2,
        tension: 0.5,
        pointRadius: 2,
      },
      {
        label: t('losses', 'Losses'),
        data: data?.map((item) => item.loss.money),
        backgroundColor: alpha(palette.red[400] as string, 0.3),
        fill: true,
        borderColor: palette.red[400],
        borderWidth: 2,
        tension: 0.5,
        pointRadius: 2,
        pointStyle: 'rectRounded',
      },
    ],
  }

  return (
    <StyledWidgetWrapper>
      <ReportWidgetsHeader
        text={t('winVsLossMoneyEquivalence', 'Win vs Loss: money equivalence')}
        explanation={t(
          'theGraphRepresentsTheComparisonOfWinnersVsLosers',
          'The graph represents the comparison of winners vs losers',
        )}
      >
        <Indicators positiveLabel={t('win', 'Win')} negativeLabel={t('loss', 'Loss')} />
      </ReportWidgetsHeader>

      {isEmpty(data) ? (
        <Box mt={4}>
          <EmptyData />
        </Box>
      ) : (
        <Box ml={spacing(-3)}>
          <Line
            data={chartData}
            options={chartOptions({ currency })}
            style={{ maxHeight: spacing(31) }}
            key={displayWidth}
          />
        </Box>
      )}
    </StyledWidgetWrapper>
  )
}
